import { useState, useEffect } from 'react';
import api from '../services/api';
import { BrainCircuit, TrendingUp, AlertTriangle, CheckCircle2, ShoppingCart, Calendar, ArrowRight, BarChart3, Clock } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

export default function InventoryInsights() {
  const [forecasting, setForecasting] = useState([]);
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    setLoading(true);
    try {
      const [fRes, tRes] = await Promise.all([
        api.get('/ai/forecasting'),
        api.get('/ai/trends')
      ]);
      setForecasting(fRes.data);
      setTrends(tRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const criticalItems = forecasting.filter(i => i.riskLevel === 'CRITICAL');
  const mediumItems = forecasting.filter(i => i.riskLevel === 'MEDIUM');

  if (loading) return (
    <div className="flex items-center justify-center h-96">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand"></div>
    </div>
  );

  return (
    <div className="space-y-8 animate-fade-in-up pb-10">
      <div className="flex justify-between items-end mb-4">
        <div>
          <h2 className="text-3xl font-black text-theme-main uppercase tracking-tight flex items-center gap-3">
            <BrainCircuit size={32} className="text-brand" /> Inventory AI Insights
          </h2>
          <p className="text-theme-muted mt-1 font-bold uppercase tracking-widest text-xs">Sales velocity and run-out predictions.</p>
        </div>
        <button onClick={loadData} className="text-brand text-xs font-black uppercase tracking-widest hover:underline">Refresh Model</button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <InsightCard 
          label="Critical Run-Out" 
          value={criticalItems.length} 
          sub="Items empty in < 3 days" 
          color="rose" 
          icon={AlertTriangle}
        />
        <InsightCard 
          label="Moderate Risk" 
          value={mediumItems.length} 
          sub="Items empty in < 7 days" 
          color="amber" 
          icon={Clock}
        />
        <InsightCard 
          label="Total Suggestions" 
          value={forecasting.filter(i => i.suggestedReorder > 0).length} 
          sub="Reorder recommendations" 
          color="emerald" 
          icon={CheckCircle2}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Run-Out Predictions Table */}
        <div className="lg:col-span-8 bg-theme-card rounded-3xl border border-theme shadow-soft overflow-hidden flex flex-col">
          <div className="p-6 border-b border-theme bg-theme-main/10 flex justify-between items-center">
            <h3 className="text-sm font-black text-theme-main uppercase tracking-widest flex items-center gap-2">
              <Calendar size={18} className="text-brand" /> Stock Longevity Forecast
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-theme-main/20 text-theme-muted text-[10px] uppercase tracking-widest font-black border-b border-theme">
                  <th className="px-6 py-4">Product Name</th>
                  <th className="px-6 py-4">Velocity (Daily)</th>
                  <th className="px-6 py-4">Current Stock</th>
                  <th className="px-6 py-4">Est. Days Left</th>
                  <th className="px-6 py-4">Smart Reorder</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-theme">
                {forecasting.slice(0, 10).map(item => (
                  <tr key={item.id} className="hover:bg-theme-main/5 transition-colors group">
                    <td className="px-6 py-4 font-bold text-theme-main text-sm">{item.name}</td>
                    <td className="px-6 py-4 text-xs font-medium text-theme-muted">{item.dailyVelocity} / day</td>
                    <td className="px-6 py-4 text-sm font-black text-theme-main">{item.stock}</td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        <span className={`font-black text-xs ${
                          item.riskLevel === 'CRITICAL' ? 'text-rose-500' :
                          item.riskLevel === 'MEDIUM' ? 'text-amber-500' :
                          'text-emerald-500'
                        }`}>
                          {item.daysRemaining > 30 ? '30+ Days' : `${item.daysRemaining} Days`}
                        </span>
                        <div className="w-24 h-1 bg-theme-main rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${item.riskLevel === 'CRITICAL' ? 'bg-rose-500' : item.riskLevel === 'MEDIUM' ? 'bg-amber-500' : 'bg-emerald-500'}`} 
                            style={{ width: `${Math.min(100, (item.daysRemaining / 14) * 100)}%` }}
                          ></div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {item.suggestedReorder > 0 ? (
                        <div className="flex items-center gap-2 text-brand">
                          <ShoppingCart size={14} />
                          <span className="font-black text-xs">Buy {item.suggestedReorder}</span>
                        </div>
                      ) : (
                        <span className="text-[10px] font-black text-emerald-500 uppercase">Optimal</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Category Performance Trends */}
        <div className="lg:col-span-4 space-y-8">
          <div className="bg-theme-card rounded-3xl border border-theme shadow-soft p-6 flex flex-col h-full">
            <h3 className="text-sm font-black text-theme-main uppercase tracking-widest flex items-center gap-2 mb-8">
              <TrendingUp size={18} className="text-brand" /> Category Trends (7d)
            </h3>
            <div className="flex-1 min-h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trends} layout="vertical">
                  <XAxis type="number" hide />
                  <YAxis dataKey="name" type="category" width={100} axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 'bold' }} />
                  <Tooltip 
                    cursor={{ fill: 'var(--bg-main)', opacity: 0.2 }}
                    contentStyle={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px' }}
                  />
                  <Bar dataKey="revenue" fill="var(--brand)" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-8 pt-6 border-t border-theme">
              <p className="text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] mb-4">Top Performers</p>
              <div className="space-y-3">
                {trends.sort((a, b) => b.revenue - a.revenue).slice(0, 3).map(t => (
                  <div key={t.name} className="flex justify-between items-center bg-theme-main/10 p-3 rounded-xl border border-theme">
                    <span className="text-xs font-bold text-theme-main">{t.name}</span>
                    <span className="text-xs font-black text-brand">${t.revenue.toFixed(0)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function InsightCard({ label, value, sub, color, icon: Icon }) {
  const colors = {
    rose: 'bg-rose-500/10 text-rose-500 border-rose-500/20 shadow-rose-500/5',
    amber: 'bg-amber-500/10 text-amber-500 border-amber-500/20 shadow-amber-500/5',
    emerald: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20 shadow-emerald-500/5',
    brand: 'bg-brand/10 text-brand border-brand/20 shadow-brand/5'
  };

  return (
    <div className={`p-6 rounded-3xl border shadow-lg flex items-center gap-5 transition-all hover:-translate-y-1 ${colors[color]}`}>
      <div className="p-4 bg-white/10 rounded-2xl">
        <Icon size={28} />
      </div>
      <div>
        <p className="text-[10px] font-black uppercase tracking-[0.2em] opacity-80">{label}</p>
        <p className="text-3xl font-black mt-1">{value}</p>
        <p className="text-[10px] font-bold mt-1 opacity-60">{sub}</p>
      </div>
    </div>
  );
}
